import { Link, useLocation, Outlet } from 'react-router-dom';
import { getCurrentUser } from '../../services/telegram-auth';
import { FiMail, FiPhone, FiMapPin, FiHeart } from 'react-icons/fi';
import { BsTelegram } from 'react-icons/bs';

const MainLayout = () => {
    const location = useLocation();
    // Получаем текущего авторизованного пользователя
    const currentUser = getCurrentUser();

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    // Определяем навигационные ссылки в зависимости от состояния авторизации
    const navLinks = currentUser
        ? [
            // Для авторизованных пользователей не показываем ссылку на главную
            { path: '/equipment', label: 'Оборудование' },
            { path: '/profile', label: 'Профиль' },
        ]
        : [
            // Убираем ссылку на главную, так как есть переход по логотипу
            { path: '/equipment', label: 'Оборудование' },
        ];

    return (
        <div className="flex min-h-screen flex-col">
            <header className="sticky top-0 z-[10000] border-b border-[#3a3a3a] backdrop-blur-lg bg-[#323232]">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center">
                                <img src="/rental-logo-white.svg" alt="rental" className="h-7" />
                            </Link>
                        </div>

                        <div className="flex items-center space-x-2">
                            {/* Мобильная навигация в правой части */}
                            <nav className="md:hidden flex space-x-1">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${isActive(link.path)
                                            ? 'text-primary bg-primary-50/20'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>

                            {/* Десктопная навигация в правой части */}
                            <nav className="hidden md:flex space-x-4">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isActive(link.path)
                                            ? 'text-primary bg-primary-50/20'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>

                            {/* Показываем информацию о пользователе или кнопку входа */}
                            {currentUser ? (
                                <div className="flex items-center">
                                    <Link to="/profile" className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium text-sm hover:bg-primary/30 transition-colors">
                                        {currentUser.first_name.charAt(0)}
                                        {currentUser.last_name ? currentUser.last_name.charAt(0) : ''}
                                    </Link>
                                </div>
                            ) : (
                                <Link
                                    to="/login"
                                    className="flex items-center text-xs md:text-sm px-3 py-1.5 md:px-4 md:py-2 bg-primary text-white rounded-full hover:bg-primary-600 transition-colors font-medium"
                                >
                                    <BsTelegram className="mr-1" /> Войти
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <footer className="border-t border-apple-100 py-12 bg-apple-50">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <h3 className="text-lg font-medium text-apple-800 mb-4 flex items-center">
                                <img src="/rental-Logo-black.svg" alt="rental" className="h-7" />
                            </h3>
                            <p className="text-apple-500 max-w-xs">
                                Профессиональное оборудование для ваших задач с удобной системой бронирования
                            </p>
                            <div className="flex items-center mt-6">
                                <FiHeart className="text-primary mr-2" />
                                <span className="text-sm text-apple-500">Калининград, с любовью к технике</span>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-medium text-apple-800 mb-4">Навигация</h3>
                            <ul className="space-y-2">
                                {/* Показываем ссылку на главную в футере только для неавторизованных пользователей */}
                                {!currentUser && (
                                    <li>
                                        <Link to="/" className="text-apple-500 hover:text-primary transition-colors">
                                            Главная
                                        </Link>
                                    </li>
                                )}
                                <li>
                                    <Link to="/equipment" className="text-apple-500 hover:text-primary transition-colors">
                                        Оборудование
                                    </Link>
                                </li>
                                {currentUser && (
                                    <li>
                                        <Link to="/profile" className="text-apple-500 hover:text-primary transition-colors">
                                            Профиль
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-medium text-apple-800 mb-4">Контакты</h3>
                            <div className="space-y-3">
                                <p className="text-apple-500 flex items-center">
                                    <FiMail className="mr-2 text-primary" /> franemamov@gmail.com
                                </p>
                                <p className="text-apple-500 flex items-center">
                                    <FiPhone className="mr-2 text-primary" /> +7 (952) 794-92-43 Максим
                                </p>
                                <p className="text-apple-500 flex items-center">
                                    <FiMapPin className="mr-2 text-primary" /> Калининград ул. Леонова 47
                                </p>
                                <div className="pt-2">
                                    <a
                                        href="https://t.me/franema_agency"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-4 py-2 bg-primary-50 text-primary rounded-lg hover:bg-primary-100 transition-colors font-medium text-sm"
                                    >
                                        <BsTelegram className="mr-1.5" /> Наш телеграм канал
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 pt-3 border-t border-apple-100 text-center text-apple-400 text-xs">
                        © {new Date().getFullYear()} ООО «Франема» ИНН 3900018775
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MainLayout; 